import { useCallback, useMemo, useState } from 'react';
import { FileItem, FolderRef } from '../types';
import { entryIds } from '../entryIdentity';
import { ROOT_FOLDER } from '../navigationModel';

export interface FileClipboard {
  /** The cut items — empty when nothing is on the clipboard. */
  items: readonly FileItem[];
  /**
   * The folder the items were cut FROM.
   *
   * Recorded at cut time because the paste site cannot recover it: the
   * clipboard deliberately survives navigation, so by the time Ctrl+V happens
   * the browser is standing somewhere else and the items' own `key` is a display
   * path, not an address (R-1). It is what makes "paste back into the folder you
   * cut from" a real no-op and everything else a real move.
   *
   * `{id: null}` while the clipboard is empty, which is never read — `paste` is
   * gated on `hasClipboard`.
   */
  cutFrom: FolderRef;
  /** Entry ids of the cut items, for dimming the source cards (R-2). */
  cutIds: ReadonlySet<string>;
  hasClipboard: boolean;
  /** Cut a set of items to the clipboard, from `origin` (replaces any previous contents). */
  cut: (items: FileItem[], origin: FolderRef) => void;
  /** Empty the clipboard (after a paste, or on Escape). */
  clear: () => void;
}

/**
 * Clipboard state for the file-browser Cut/Paste gesture (#980).
 *
 * Stores the full `FileItem[]`, not just ids: selection is per-directory and
 * clears on navigation, but the clipboard must SURVIVE navigation so the user
 * can Ctrl+X here, navigate elsewhere, and Ctrl+V to move the items in. Keeping
 * the whole item (not the id) means the paste-side `onMoveMany` has everything
 * it needs without re-resolving anything against a directory we've since left.
 *
 * ### Why the ORIGIN is stored too (STD-029 B-4 review)
 *
 * `canMoveInto` refuses a pure no-op — a paste into the folder the items already
 * live in — and it needs the origin to know. B-4's first cut passed a literal
 * `{id: null}` there, on the reasoning that the origin "is not knowable at the
 * paste site". It was not knowable, and the placeholder was not inert: `{id:
 * null}` **is** the caller's own organization root, so any paste made while
 * `currentFolder.id` was null compared `null === null`, tripped the no-op guard,
 * and did nothing at all — no request, no error, no toast.
 *
 * **How narrow that window actually is, stated exactly, because the first
 * account of this bug was wrong in the alarming direction.** Every *resolved*
 * crumb carries a real node id (`crumbRefs` reads `node.object_id`), so a
 * settled browser never holds `{id: null}` and a normal paste-at-root was fine.
 * What does hold it is the **placeholder crumb `crumbRefs` returns while the
 * first listing is still in flight** — click Root during that window with a
 * clipboard in hand and the paste is silently refused. Timing-dependent, which
 * is why no browser assertion can pin it: the spec passes with the placeholder
 * restored, and saying otherwise would be claiming a proof this repo does not
 * have. The unit cases below the guard are the real pin.
 *
 * Recording the origin at cut time removes the window rather than narrowing it,
 * and it costs one field. A sentinel would not have worked: the ref type has
 * exactly two inhabitants (an id, or the root), and any third value would have
 * to be invented and then remembered at every comparison.
 */
export function useFileClipboard(): FileClipboard {
  const [items, setItems] = useState<readonly FileItem[]>([]);
  const [cutFrom, setCutFrom] = useState<FolderRef>(ROOT_FOLDER);

  const cut = useCallback((next: FileItem[], origin: FolderRef) => {
    setItems(next);
    setCutFrom(origin);
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    // The origin is deliberately NOT reset to a distinguishable value. It is
    // read only while `hasClipboard`, and leaving the last one in place keeps
    // this hook's state a pair rather than a pair-plus-a-validity-flag.
    setCutFrom(ROOT_FOLDER);
  }, []);

  const cutIds = useMemo(() => new Set(entryIds(items)), [items]);

  return {
    items,
    cutFrom,
    cutIds,
    hasClipboard: items.length > 0,
    cut,
    clear,
  };
}
